// Judge detail page.
//
// GET /api/judges/:id returns the Judge plus the linked matters in the
// viewer's scope (the firm for admin+). Hero card shows name/title/court/
// contact; the linked-matter list below is clickable rows to /matter/:eID.
// If the endpoint 404s (or the id isn't a valid Mongo ObjectId), the hook
// returns a null Judge and an empty matters array and the page renders a
// "Judge not found" card without crashing.

import { useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { fetchJudgeDetail } from "../../api/mock";
import { practiceAreaById, practiceAreaLabel } from "../../data/practice-areas";
import { caseKindLabel, stageLabel } from "../../lib/caseLabels";
import { recordStep, usePageTrail } from "../chrome/trail";
import { EntityLink } from "../chrome/EntityLink";
import { Skeleton, SkeletonGroup } from "../chrome/Skeleton";
import { SEP } from "../chrome/SepList";
import { EmptyCopy } from "../chrome/EmptyCopy";

export function JudgeDetail() {
  const navigate = useNavigate();
  const { judgeId } = useParams<{ judgeId: string }>();
  const { data, isLoading } = useQuery({
    queryKey: ["judge", judgeId ?? ""],
    queryFn: () => (judgeId ? fetchJudgeDetail(judgeId) : Promise.resolve(null)),
    enabled: !!judgeId,
  });
  usePageTrail([{ label: "Judges" }, recordStep(data?.judge?.name, isLoading, "Judge")]);

  if (isLoading) {
    return (
      <div className="main">
        <div style={{ padding: 18 }}>
          <SkeletonGroup label="Loading judge">
            <Skeleton width="35%" height={20} />
            <Skeleton width="55%" height={12} />
            <div style={{ height: 8 }} />
            <Skeleton width="100%" height={120} radius={10} />
          </SkeletonGroup>
        </div>
      </div>
    );
  }
  if (!data || !data.judge) {
    return (
      <div className="main">
        <div className="detail-body">
          <div className="cc-card" style={{ padding: 18 }}>
            <p style={{ color: "var(--muted)", fontSize: 12 }}>
              Judge <code>{judgeId}</code> not found.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const j = data.judge;
  const matters = data.matters;

  return (
    <div className="main">
      <div className="detail-body">
        <div className="cc-card" style={{ padding: 18 }}>
          <div style={{ fontSize: 18, fontWeight: 600, color: "var(--ink)" }}>{j.name}</div>
          <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4 }}>
            {[j.title, j.court, j.district].filter(Boolean).join(" · ") || "—"}
          </div>
          <div
            style={{
              fontSize: 12,
              color: "var(--ink-2)",
              marginTop: 8,
              display: "flex",
              gap: 14,
              flexWrap: "wrap",
            }}
          >
            {j.contactEmail ? (
              <a className="cc-link" href={`mailto:${j.contactEmail}`}>
                {j.contactEmail}
              </a>
            ) : null}
            {j.contactPhone ? (
              <a className="cc-link" href={`tel:${j.contactPhone}`}>
                {j.contactPhone}
              </a>
            ) : null}
          </div>
          {j.notes ? (
            <div
              style={{ fontSize: 12, color: "var(--ink-2)", marginTop: 10, fontStyle: "italic" }}
            >
              {j.notes}
            </div>
          ) : null}
        </div>

        <div className="cc-card" style={{ marginTop: 14 }}>
          <div className="cc-card-head">
            <div className="cc-card-title">Matters before this judge</div>
            <div className="cc-card-sub">· {matters.length}</div>
          </div>
          <div>
            {matters.length === 0 ? (
              <div className="cc-empty">
                <div className="icon" aria-hidden>
                  ⚖
                </div>
                <EmptyCopy
                  title="No matters before this judge yet"
                  mine="No matters assigned to you are before this judge"
                  body="Matters linked to this judge appear here."
                />
              </div>
            ) : (
              matters.map((m) => {
                const pa = practiceAreaById(m.practiceArea);
                const kind = caseKindLabel(m);
                return (
                  <div
                    key={m.eID}
                    className="row clickable cc-row-lift"
                    onClick={() => navigate(`/matter/${m.eID}`)}
                    style={{
                      display: "grid",
                      gridTemplateColumns: "auto 1fr auto",
                      gap: 14,
                      padding: "12px 18px",
                      borderTop: "1px solid var(--line-2)",
                      alignItems: "center",
                      cursor: "pointer",
                    }}
                  >
                    <span className="pa-dot lg" style={{ background: pa?.accent }} />
                    <div>
                      <div className="row-title">
                        {pa ? (
                          <EntityLink kind="practiceArea" id={pa.id} stopPropagation subtle>
                            {pa.label}
                          </EntityLink>
                        ) : (
                          practiceAreaLabel(m.practiceArea)
                        )}
                        {kind ? SEP + kind : ""}
                      </div>
                      <div className="row-sub">{stageLabel(m.stage)}</div>
                    </div>
                    <EntityLink
                      kind="matter"
                      id={m.eID}
                      eID={m.eID}
                      stopPropagation
                      subtle
                      style={{
                        fontFamily: "var(--font-mono)",
                        fontSize: 11,
                        color: "var(--ink-2)",
                      }}
                    >
                      {m.eID}
                    </EntityLink>
                  </div>
                );
              })
            )}
          </div>
        </div>

        <div className="cc-detail-footer">
          {j.updatedAt ? (
            <>
              <span>
                Last updated{SEP}
                {j.updatedAt.slice(0, 10)}
              </span>
              <span className="sep">{SEP.trim()}</span>
            </>
          ) : null}
          <span>
            {matters.length} linked matter{matters.length === 1 ? "" : "s"}
          </span>
          {j.court ? (
            <>
              <span className="sep">·</span>
              <span>{j.court}</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
